/**
 * @module RecruitingDashboardUnified
 * @description Unified Recruiting Dashboard — combined Fest + Flex KPIs,
 *   Pipeline-Funnel, Einsatz-Übersicht, Quellen-Analyse, Monatstrend.
 *   Filter-Tabs (Gesamt/Fest/Flex) trigger re-fetch with kategorie param.
 * @route /recruiting
 * @since v2.246.0
 * @updated v2.248.0 — KPIs filter per tab, Flex-Pipeline card
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, UserPlus, Briefcase, CalendarCheck } from 'lucide-react';
import KpiTooltip from '../components/KpiTooltip';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

const DONUT_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#E5E7EB', '#10B981', '#F59E0B'];

// #959 Bundle A: Status = Single Source of Truth (Spec aus FlexBewerberDetail).
// Reihenfolge entspricht Pipeline-Fortschritt (beworben → aktiv) + Endstatus.
const STATUS_DEF = [
  { key: 'beworben',   color: '#338edc', sort: 1 },
  { key: 'in_review',  color: '#F59E0B', sort: 2 },
  { key: 'eingeladen', color: '#22D3EE', sort: 3 },
  { key: 'zugesagt',   color: '#22D3EE', sort: 4 },
  { key: 'aktiv',      color: '#10B981', sort: 5 },
  { key: 'abgelehnt',  color: '#EF4444', sort: 6 },
  { key: 'inaktiv',    color: '#9CA3AF', sort: 7 },
];

export default function RecruitingDashboardUnified() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [flexDash, setFlexDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewFilter, setViewFilter] = useState('gesamt');

  const load = useCallback(async (filter) => {
    setLoading(true);
    try {
      const katParam = filter && filter !== 'gesamt' ? `&kategorie=${filter}` : '';
      const [dRes, fRes] = await Promise.all([
        f(`/api/recruiting/dashboard?periode=30${katParam}`),
        f('/api/flex/dashboard?periode=30'),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (fRes.ok) setFlexDash(await fRes.json());
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(viewFilter); }, [viewFilter, load]);

  if (loading && !dashboard) {
    return (
      <div className="p-8 text-center" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
        <div className="inline-block w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dk = dashboard?.kpis || {};
  const fk = flexDash?.kpis || {};

  // KPIs based on filter — #959 Bundle A: Status = Single Source of Truth, Pipeline-Wording entfernt
  const tipFlexPool = t('recruiting.unified.kpi.flexPoolTip', 'Flex-Bewerber mit Status aktiv — bereit für Einsätze');
  const tipAktive = t('recruiting.unified.kpi.aktiveBewerberTip', 'Bewerber mit Status ungleich abgelehnt/inaktiv');
  const tipBewerber = t('recruiting.unified.kpi.bewerberTip', 'Alle Bewerber im Zeitraum (inkl. abgelehnt + inaktiv)');
  const tipPipeline = t('recruiting.unified.kpi.pipelineTip', 'Bewerber im laufenden Prozess (Status ungleich aktiv/abgelehnt/inaktiv)');
  const tipEingestellt = t('recruiting.unified.kpi.eingestelltTip', 'Festanstellungen im 30-Tage-Fenster');
  const tipOffeneStellen = t('recruiting.unified.kpi.offeneStellenTip', 'Aktive Stellenanzeigen (recruiting_stellen.status=aktiv)');
  const tipEinsaetze = t('recruiting.unified.kpi.einsaetzeTip', 'Anzahl Flex-Einsätze in den letzten 7 Tagen');

  const offeneStellenCount = dk.offene_stellen_count ?? dk.offene_stellen ?? 0;

  const kpiCards = viewFilter === 'flex' ? [
    { label: t('recruiting.unified.kpi.bewerber', 'Bewerber'), value: dk.gesamt || 0, icon: Users, color: '#EC4899', tip: tipBewerber },
    { label: t('recruiting.unified.kpi.flexPool', 'Flex-Pool'), value: dk.flex_pool || 0, icon: Briefcase, color: '#8B5CF6', tip: tipFlexPool },
    { label: t('recruiting.unified.kpi.aktiveBewerber', 'Aktive Bewerber'), value: dk.aktive_bewerber || 0, icon: UserPlus, color: '#10B981', tip: tipAktive },
    { label: t('recruiting.unified.kpi.einsaetze', 'Einsätze (7T)'), value: fk.einsaetze_count || 0, icon: CalendarCheck, color: '#10B981', tip: tipEinsaetze },
  ] : viewFilter === 'fest' ? [
    { label: t('recruiting.unified.kpi.bewerber', 'Bewerber'), value: dk.gesamt || 0, icon: Users, color: '#EC4899', tip: tipBewerber },
    { label: t('recruiting.unified.kpi.pipeline', 'In Pipeline'), value: dk.pipeline || 0, icon: UserPlus, color: '#EC4899', tip: tipPipeline },
    { label: t('recruiting.unified.kpi.eingestellt', 'Eingestellt (30T)'), value: dk.eingestellt_monat || 0, icon: CalendarCheck, color: '#10B981', tip: tipEingestellt },
    { label: t('recruiting.unified.kpi.offeneStellen', 'Offene Stellen'), value: offeneStellenCount, icon: Briefcase, color: '#8B5CF6', tip: tipOffeneStellen },
  ] : [
    { label: t('recruiting.unified.kpi.bewerber', 'Bewerber'), value: dk.gesamt || 0, icon: Users, color: '#EC4899', tip: tipBewerber },
    { label: t('recruiting.unified.kpi.aktiveBewerber', 'Aktive Bewerber'), value: dk.aktive_bewerber || 0, icon: UserPlus, color: '#10B981', tip: tipAktive },
    { label: t('recruiting.unified.kpi.flexPool', 'Flex-Pool'), value: dk.flex_pool || 0, icon: Briefcase, color: '#8B5CF6', tip: tipFlexPool },
    { label: t('recruiting.unified.kpi.eingestellt', 'Eingestellt (30T)'), value: dk.eingestellt_monat || 0, icon: CalendarCheck, color: '#10B981', tip: tipEingestellt },
  ];

  // #959 Bundle A: status_verteilung statt funnel — Status = Single Source of Truth.
  // Backend liefert pro Status: count, count_fest, count_flex (nach Test-Filter).
  // Im Gesamt-Tab nutzen wir count_fest/count_flex separat für Fest-/Flex-Card,
  // im Fest-/Flex-Tab ist Backend bereits gefiltert (count_fest/_flex == count).
  const statusVerteilung = dashboard?.status_verteilung || [];
  const buildStatusData = (countField) => {
    const byKey = Object.fromEntries(statusVerteilung.map(r => [r.status, r]));
    return STATUS_DEF.map(def => {
      const row = byKey[def.key] || {};
      return { key: def.key, color: def.color, count: parseInt(row[countField] ?? row.count ?? 0) };
    });
  };
  const festStatusData = buildStatusData('count_fest');
  const flexStatusData = buildStatusData('count_flex');
  const quellen = dashboard?.quellen || [];
  const monatlich = dashboard?.monatlich || [];
  const upcoming = flexDash?.kommende_einsaetze || [];
  const offeneStellen = dashboard?.offene_stellen || [];
  const naechsteInterviews = dashboard?.naechste_interviews || [];

  const quellenData = quellen.map((q, i) => ({
    name: q.quelle,
    value: parseInt(q.count),
    fill: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  const formatMonat = (m) => {
    if (!m) return m;
    const [y, mo] = m.split('-');
    const date = new Date(parseInt(y), parseInt(mo) - 1);
    return date.toLocaleDateString('de-AT', { month: 'short', year: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-lg p-3">
        <p className="text-xs font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{d.name}</p>
        <p className="text-sm font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{d.value}</p>
      </div>
    );
  };

  // #959 Bundle A: Status-basierter Funnel statt Pipeline-Stufen.
  // Reihenfolge fix nach STATUS_DEF (beworben → aktiv + Endstatus). Conv-Rate nur für
  // Aktiv-Status (beworben→in_review→eingeladen→zugesagt→aktiv); Endstatus (abgelehnt/inaktiv)
  // werden ohne Konversion dargestellt.
  const renderStatusDistribution = (data, title) => {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const ENDSTATUS = new Set(['abgelehnt', 'inaktiv']);
    const visible = data.filter(d => d.count > 0 || !ENDSTATUS.has(d.key));
    return (
      <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary, #4B5563)' }}>{title}</h3>
        <div className="space-y-2">
          {visible.map((s, i, arr) => {
            const pct = (s.count / maxCount) * 100;
            const isEnd = ENDSTATUS.has(s.key);
            const prev = i > 0 ? arr[i - 1] : null;
            const showConv = !isEnd && prev && !ENDSTATUS.has(prev.key) && prev.count > 0;
            const convPct = showConv ? Math.round((s.count / prev.count) * 100) : null;
            const label = t(`recruiting.status.${s.key}`, s.key);
            return (
              <div key={s.key} className="flex items-center gap-3">
                <span className="text-xs w-32 text-right flex-shrink-0 font-medium truncate" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {label}
                </span>
                <div className="flex-1 h-7 rounded-lg overflow-hidden" style={{ background: 'var(--bg-inset, #F3F4F6)' }}>
                  <div className="h-full rounded-lg flex items-center px-2 transition-all" style={{ width: `${Math.max(pct, 4)}%`, background: s.color }}>
                    <span className="text-xs font-bold text-white">{s.count}</span>
                  </div>
                </div>
                {convPct !== null ? (
                  <span className="text-xs flex-shrink-0 w-12 text-right" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                    {convPct}%
                  </span>
                ) : (
                  <span className="text-xs flex-shrink-0 w-12 text-right" style={{ color: 'var(--text-muted, #9CA3AF)' }}>—</span>
                )}
              </div>
            );
          })}
          {total === 0 && (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              {t('recruiting.unified.noData', 'Keine Daten')}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('recruiting.unified.title', 'Recruiting')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('recruiting.unified.subtitle', 'Übersicht über alle Bewerbungen und Personalgewinnung')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/recruiting/bewerber?create=1')}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
            style={{ background: '#338edc' }}>
            + {t('recruiting.unified.neuerBewerber', 'Neuer Bewerber')}
          </button>
        {/* View filter toggle */}
        <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
          {[
            { key: 'gesamt', label: t('recruiting.unified.filterGesamt', 'Gesamt') },
            { key: 'fest', label: t('recruiting.unified.filterFest', 'Festanstellung') },
            { key: 'flex', label: t('recruiting.unified.filterFlex', 'Flex-Worker') },
          ].map(tb => (
            <button key={tb.key} onClick={() => setViewFilter(tb.key)}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                viewFilter === tb.key
                  ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]'
                  : 'hover:text-[var(--text-primary)]'
              }`}
              style={{ color: viewFilter === tb.key ? 'var(--text-primary, #1A1A2E)' : 'var(--text-secondary, #4B5563)' }}>
              {tb.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map((kpi, i) => (
          <KpiTooltip key={i} text={kpi.tip}>
            <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-4 cursor-help" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
              <div className="flex items-center gap-2 mb-1">
                <kpi.icon size={16} style={{ color: kpi.color }} />
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{kpi.label}</p>
              </div>
              <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{kpi.value}</p>
            </div>
          </KpiTooltip>
        ))}
      </div>

      {/* Row: Status-Verteilung (Fest + Flex) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(viewFilter === 'gesamt' || viewFilter === 'fest') && renderStatusDistribution(
          festStatusData,
          t('recruiting.unified.festStatus', 'Fest — Status-Verteilung')
        )}

        {(viewFilter === 'gesamt' || viewFilter === 'flex') && renderStatusDistribution(
          flexStatusData,
          t('recruiting.unified.flexStatus', 'Flex — Status-Verteilung')
        )}
      </div>

      {/* Row: Flex Einsätze (only in gesamt + flex) */}
      {(viewFilter === 'gesamt' || viewFilter === 'flex') && (
        <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.unified.flexEinsaetze', 'Flex-Einsätze (7 Tage)')}
            </h3>
            <button onClick={() => navigate('/recruiting/einsaetze')}
              className="text-xs font-medium" style={{ color: '#338edc' }}>
              {t('common.showAll', 'Alle anzeigen')} →
            </button>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              {t('recruiting.unified.noAssignments', 'Keine Einsätze geplant')}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {upcoming.slice(0, 6).map(e => (
                <div key={e.id} onClick={() => navigate(`/recruiting/einsaetze/${e.id}`)}
                  className="flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
                  style={{ borderColor: 'var(--border-card, #E5E7EB)', borderLeft: `3px solid ${e.dringend ? '#EF4444' : e.status === 'voll' ? '#10B981' : '#F59E0B'}` }}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{e.titel}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                      {new Date(e.datum_von).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })} · {e.uhrzeit_von?.slice(0, 5)}–{e.uhrzeit_bis?.slice(0, 5)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold flex-shrink-0 tabular-nums" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                    {e.zugesagt}/{e.benoetigt}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Row: Quellen + Monatstrend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quellen-Analyse */}
        <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {t('recruiting.unified.quellen', 'Quellen-Analyse')}
          </h3>
          {quellenData.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              {t('recruiting.unified.noData', 'Keine Daten')}
            </p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-[140px] h-[140px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={quellenData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                      {quellenData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {quellenData.map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: q.fill }} />
                    <span className="text-sm capitalize" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{q.name}</span>
                    <span className="text-xs ml-auto tabular-nums" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{q.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Monatstrend */}
        <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {t('recruiting.unified.trend', 'Monatlicher Trend')}
          </h3>
          {monatlich.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
              {t('recruiting.unified.noData', 'Keine Daten')}
            </p>
          ) : (
            <div className="space-y-3">
              {monatlich.map(m => {
                const maxM = Math.max(...monatlich.map(x => parseInt(x.eingegangen)), 1);
                return (
                  <div key={m.monat}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: 'var(--text-secondary, #4B5563)' }}>{formatMonat(m.monat)}</span>
                      <span style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {m.eingegangen} / {m.eingestellt}
                      </span>
                    </div>
                    <div className="flex gap-1 h-4">
                      <div className="rounded" style={{ width: `${(parseInt(m.eingegangen) / maxM) * 100}%`, background: '#338edc', minWidth: parseInt(m.eingegangen) > 0 ? 4 : 0 }} />
                      <div className="rounded" style={{ width: `${(parseInt(m.eingestellt) / maxM) * 100}%`, background: '#10B981', minWidth: parseInt(m.eingestellt) > 0 ? 4 : 0 }} />
                    </div>
                  </div>
                );
              })}
              <div className="flex gap-4 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded" style={{ background: '#338edc' }} />
                  <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('recruiting.unified.eingegangen', 'Eingegangen')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded" style={{ background: '#10B981' }} />
                  <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('recruiting.unified.eingestellt', 'Eingestellt')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row: Interviews + Offene Stellen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {naechsteInterviews?.length > 0 && (viewFilter === 'gesamt' || viewFilter === 'fest') && (
          <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.unified.naechsteInterviews', 'Nächste Interviews')}
            </h3>
            <div className="space-y-2">
              {naechsteInterviews.map(iv => (
                <div key={iv.id} className="flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                  style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
                  onClick={() => navigate(`/recruiting/bewerber/${iv.bewerber_id}`)}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{iv.vorname} {iv.nachname}</p>
                    <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                      {iv.interviewer_name && `${iv.interviewer_name} · `}{iv.typ || 'vor_ort'}
                    </span>
                  </div>
                  <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                    {iv.datum ? new Date(iv.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {offeneStellen.length > 0 && (
          <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {t('recruiting.unified.offeneStellen', 'Offene Stellen')}
              </h3>
              <button onClick={() => navigate('/recruiting/stellen')} className="text-xs font-medium" style={{ color: '#338edc' }}>
                {t('common.showAll', 'Alle anzeigen')} →
              </button>
            </div>
            <div className="space-y-2">
              {offeneStellen.map(s => (
                <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{s.titel}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {s.typ && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: s.typ === 'flex' ? '#ECFEFF' : '#EBF4FD', color: s.typ === 'flex' ? '#0891B2' : '#338edc' }}>
                          {s.typ === 'flex' ? 'Flex' : 'Fest'}
                        </span>
                      )}
                      <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{s.bewerber_count || 0} {t('recruiting.unified.bewerber', 'Bewerber')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
